import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { authClient } from "../../lib/auth-client";
import { useLogout } from "../../hooks/useLogout";
import { isRedditAttributed } from "../../providers/RedditPixelIdentifier";
import {
  Box,
  Button,
  Stack,
  Typography,
  CircularProgress,
  Container,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckRounded from "@mui/icons-material/CheckRounded";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { isNative } from "../../lib/platform";
import { AuthLoadingScreen } from "../auth/AuthLoadingScreen";
import { AuthPageLayout, AuthStatusBadge } from "../auth/AuthPageLayout";
import {
  makeAuthColors,
  makeGlassButtonSx,
  primaryButtonSx,
} from "../auth/authStyles";

// Adaptive polling intervals: start fast (2s), then slow down
// Pattern: 2s x3, 5s x3, 10s x6 = ~90 seconds total coverage
const POLLING_INTERVALS_MS = [
  2000,
  2000,
  2000, // First 6 seconds: check every 2s (most webhooks complete here)
  5000,
  5000,
  5000, // Next 15 seconds: check every 5s
  10000,
  10000,
  10000, // Next 30 seconds: check every 10s
  10000,
  10000,
  10000, // Final 30 seconds: check every 10s
];

export const MembershipSuccess: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { data, isPending, refetch } = authClient.useSession();
  const { mutate: logout } = useLogout();
  const [pollingCount, setPollingCount] = useState(0);
  const hasTrackedPurchase = useRef(false);

  const isMembershipActive = data?.session?.is_membership_active ?? false;
  const isAuthenticated = !!data?.user;
  const userId = data?.user?.id;
  const MAX_POLLING_ATTEMPTS = POLLING_INTERVALS_MS.length;

  // Redirect to sign-in if session is revoked (becomes unauthenticated)
  // This only happens if membership is DEACTIVATED (not activated)
  useEffect(() => {
    if (!isPending && !isAuthenticated) {
      // Session was revoked - redirect to sign in to get fresh session
      navigate("/sign-in", {
        replace: true,
        state: { message: "Please sign in again to continue" },
      });
    }
  }, [isAuthenticated, isPending, navigate]);

  // Track Reddit Purchase conversion when membership becomes active
  useEffect(() => {
    if (
      isMembershipActive &&
      userId &&
      isRedditAttributed() &&
      !hasTrackedPurchase.current
    ) {
      window.rdt?.("track", "Purchase", {
        conversion_id: `purchase_${userId}`,
      });
      hasTrackedPurchase.current = true;
    }
  }, [isMembershipActive, userId]);

  // Navigate when membership becomes active
  useEffect(() => {
    if (!isPending && isMembershipActive) {
      navigate("/dashboard");
    }
  }, [isMembershipActive, isPending, navigate]);

  // Poll for membership activation with forced cookie refresh
  // Uses adaptive polling: fast initially, then slows down
  useEffect(() => {
    if (isPending || isMembershipActive) return;

    let timeoutId: NodeJS.Timeout;

    const poll = async () => {
      // Force cookie refresh by bypassing cache
      // This fetches from DB and updates the cookie with latest claims
      await authClient.getSession({
        query: {
          disableCookieCache: true,
        },
      });

      // Then refetch the hook to update UI
      refetch();

      setPollingCount((prev) => {
        const nextCount = prev + 1;

        // Schedule next poll if we haven't exceeded max attempts
        if (nextCount < MAX_POLLING_ATTEMPTS) {
          const nextInterval = POLLING_INTERVALS_MS[nextCount] ?? 10000;
          timeoutId = setTimeout(poll, nextInterval);
        }

        return nextCount;
      });
    };

    // Start first poll after initial interval
    const initialInterval = POLLING_INTERVALS_MS[0] ?? 2000;
    timeoutId = setTimeout(poll, initialInterval);

    return () => clearTimeout(timeoutId);
  }, [isPending, isMembershipActive, refetch]);

  const isDark = theme.palette.mode === "dark";
  const { textSecondary } = makeAuthColors(isDark);

  // Native gets the auth journey's aurora treatment (the paywall it arrives
  // from already lives on it); web keeps the original layout, which handles
  // the Lemon Squeezy checkout return differently.
  const native = isNative();

  // Loading state
  if (isPending) {
    if (native) {
      return <AuthLoadingScreen />;
    }
    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor:
            theme.palette.mode === "dark" ? "#1a1a1a" : "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
          }}
        >
          <CircularProgress size={40} sx={{ color: "#22c55e" }} />
          <Typography
            sx={{
              color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
              fontSize: "1rem",
            }}
          >
            Loading your membership status...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Success state - membership is active
  if (isMembershipActive) {
    if (native) {
      return (
        <AuthPageLayout
          contentKey="active"
          topSlot={
            <AuthStatusBadge tone="success">
              <CheckRounded sx={{ fontSize: 36 }} />
            </AuthStatusBadge>
          }
          headline="You're all set"
          supporting="Your membership is active. Taking you to your dashboard."
        >
          <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "center" } }}>
            <CircularProgress size={24} sx={{ color: "#22c55e" }} />
          </Box>
        </AuthPageLayout>
      );
    }
    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor:
            theme.palette.mode === "dark" ? "#1a1a1a" : "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Container maxWidth="sm">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 4,
              textAlign: "center",
            }}
          >
            {/* Logo */}
            <img
              src="/insightLongLogoPrimaryWhiteText.svg"
              alt="Insight Analytics"
              style={{
                height: 48,
                width: "auto",
              }}
            />

            {/* Success Icon */}
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                backgroundColor: "rgba(34, 197, 94, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 48, color: "#22c55e" }} />
            </Box>

            {/* Success Message */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "#22c55e",
                  fontSize: { xs: "1.75rem", md: "2rem" },
                }}
              >
                Welcome to Insight
              </Typography>
              <Typography
                sx={{
                  color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                  fontSize: "1rem",
                }}
              >
                Redirecting to your dashboard...
              </Typography>
            </Box>

            <CircularProgress size={24} sx={{ color: "#22c55e" }} />
          </Box>
        </Container>
      </Box>
    );
  }

  // Main state - waiting for activation
  if (native) {
    const delayed = pollingCount >= MAX_POLLING_ATTEMPTS;
    return (
      <AuthPageLayout
        contentKey={delayed ? "delayed" : "activating"}
        topSlot={
          delayed ? (
            <AuthStatusBadge tone="warning">
              <WarningAmberIcon sx={{ fontSize: 32 }} />
            </AuthStatusBadge>
          ) : (
            <AuthStatusBadge tone="success">
              <CircularProgress size={30} color="inherit" />
            </AuthStatusBadge>
          )
        }
        headline={delayed ? "Activation delayed" : "Activating your membership"}
        supporting={
          delayed
            ? "Your payment was successful, but activation is taking longer than expected. Give it a few minutes, then refresh."
            : "This usually takes a few seconds."
        }
      >
        {!delayed && (
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "flex-start", sm: "center" },
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1.25,
                px: 2,
                py: 0.75,
                borderRadius: 999,
                backgroundColor: isDark
                  ? "rgba(255, 255, 255, 0.06)"
                  : "rgba(255, 255, 255, 0.5)",
                backdropFilter: "blur(12px)",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#22c55e",
                  animation: "membershipPulse 1.5s ease-in-out infinite",
                  "@keyframes membershipPulse": {
                    "0%, 100%": { opacity: 1 },
                    "50%": { opacity: 0.4 },
                  },
                }}
              />
              <Typography sx={{ color: textSecondary, fontSize: "0.875rem" }}>
                Checking payment status ({pollingCount}/{MAX_POLLING_ATTEMPTS})
              </Typography>
            </Box>
          </Box>
        )}

        <Stack spacing={1.5}>
          {delayed && (
            <Button fullWidth onClick={() => window.location.reload()} sx={primaryButtonSx}>
              Refresh status
            </Button>
          )}
          {!delayed && (
            <Button
              fullWidth
              onClick={() => window.location.reload()}
              sx={makeGlassButtonSx(isDark)}
            >
              Refresh status
            </Button>
          )}
          <Button fullWidth onClick={() => logout()} sx={makeGlassButtonSx(isDark)}>
            Sign out
          </Button>
        </Stack>

        <Typography
          sx={{
            mt: 2.5,
            fontSize: "0.8125rem",
            color: textSecondary,
            textAlign: { xs: "left", sm: "center" },
          }}
        >
          If this keeps happening, contact support with your order confirmation.
        </Typography>
      </AuthPageLayout>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: theme.palette.mode === "dark" ? "#1a1a1a" : "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: 4, md: 5 },
            textAlign: "center",
          }}
        >
          {/* Logo */}
          <img
            src="/insightLongLogoPrimaryWhiteText.svg"
            alt="Insight Analytics"
            style={{
              height: 48,
              width: "auto",
            }}
          />

          {/* Status Card */}
          <Box
            sx={{
              width: "100%",
              borderRadius: 3,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.02)"
                  : "rgba(0, 0, 0, 0.02)",
              border:
                theme.palette.mode === "dark"
                  ? "1px solid rgba(255, 255, 255, 0.1)"
                  : "1px solid rgba(0, 0, 0, 0.1)",
              p: { xs: 3, md: 4 },
            }}
          >
            {pollingCount < MAX_POLLING_ATTEMPTS && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                {/* Processing Animation */}
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    backgroundColor: "rgba(34, 197, 94, 0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={32} sx={{ color: "#22c55e" }} />
                </Box>

                {/* Title */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color:
                        theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
                      fontSize: { xs: "1.25rem", md: "1.5rem" },
                    }}
                  >
                    Activating Your Membership
                  </Typography>
                  <Typography
                    sx={{
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                      fontSize: "0.9375rem",
                    }}
                  >
                    This usually takes a few seconds
                  </Typography>
                </Box>

                {/* Progress indicator */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.05)"
                        : "rgba(0, 0, 0, 0.05)",
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "#22c55e",
                      animation: "pulse 1.5s ease-in-out infinite",
                      "@keyframes pulse": {
                        "0%, 100%": { opacity: 1 },
                        "50%": { opacity: 0.4 },
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                      fontSize: "0.875rem",
                    }}
                  >
                    Checking payment status ({pollingCount}/
                    {MAX_POLLING_ATTEMPTS})
                  </Typography>
                </Box>
              </Box>
            )}

            {pollingCount >= MAX_POLLING_ATTEMPTS && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                {/* Warning Icon */}
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    backgroundColor: "rgba(245, 158, 11, 0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <WarningAmberIcon sx={{ fontSize: 32, color: "#f59e0b" }} />
                </Box>

                {/* Title */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color:
                        theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
                      fontSize: { xs: "1.25rem", md: "1.5rem" },
                    }}
                  >
                    Activation Delayed
                  </Typography>
                  <Typography
                    sx={{
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                      fontSize: "0.9375rem",
                      maxWidth: 400,
                      mx: "auto",
                    }}
                  >
                    Your payment was successful, but activation is taking longer
                    than expected. Please wait a few minutes and try refreshing.
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              sx={{
                backgroundColor: "#22c55e",
                color: "white",
                fontWeight: 600,
                py: 1.5,
                px: 3,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "0.9375rem",
                "&:hover": {
                  backgroundColor: "#16a34a",
                },
              }}
            >
              Refresh Status
            </Button>
            <Button
              variant="outlined"
              onClick={() => logout()}
              sx={{
                borderColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.2)"
                    : "rgba(0, 0, 0, 0.2)",
                color: theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
                fontWeight: 500,
                py: 1.5,
                px: 3,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "0.9375rem",
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.05)",
                  borderColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.3)"
                      : "rgba(0, 0, 0, 0.3)",
                },
              }}
            >
              Sign Out
            </Button>
          </Box>

          {/* Support Text */}
          <Typography
            sx={{
              color: theme.palette.mode === "dark" ? "#6b7280" : "#9ca3af",
              fontSize: "0.8125rem",
              maxWidth: 400,
            }}
          >
            If you continue to experience issues, please contact support with
            your order confirmation.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};
